import type { JSONContent } from '@tiptap/react';

import { calendarZone, localInput } from './calendar';
import { blockText } from './export';
import type { EntityValue, Kind } from './model';

/**
 * How a sync conflict is put in front of the owner.
 *
 * A conflict is a record this device edited while another device saved a
 * different version of it. The server holds the edit back rather than letting
 * the later arrival silently win (see `reconcile` in `server/store.ts`), and
 * both clients show the two versions side by side so the owner can choose.
 * These functions turn the two values into the rows that comparison shows.
 * They are the reference for `ConflictReport.swift`, which is checked against
 * `ios/CairnKit/Tests/Fixtures/conflict.json`; change both together.
 */

/** One property that reads differently in the two versions. */
export type ConflictField = {
  /** Stable name of the property, such as `title` or `card.number`. */
  key: string;
  label: string;
  /**
   * `text` values are shown side by side, `long` values as a line comparison
   * from `conflictLines`, and `secret` values masked until revealed.
   */
  format: 'text' | 'long' | 'secret';
  /** This device's reading; an empty string means the property is unset. */
  local: string;
  /** The other device's reading, as the server holds it. */
  remote: string;
};

export type ConflictLine = { side: 'both' | 'local' | 'remote'; text: string };

type Value = Record<string, unknown>;

type Reader = {
  key: string;
  label: string;
  format?: ConflictField['format'];
  /** Stored properties the row reads, when that is not just `key`. */
  covers?: string[];
  read: (value: Value) => string;
};

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const vaultTypes: Record<string, string> = {
  login: 'Login',
  development: 'Development secret',
  recovery: 'Recovery codes',
  file: 'Secure file',
  card: 'Credit card',
  ssh: 'SSH keys',
};

const calendarTypes: Record<string, string> = {
  booking: 'Appointment',
  availability: 'Availability',
  birthday: 'Birthday',
};

const kindLabels: Record<Kind, string> = {
  note: 'Note',
  task: 'Task',
  vault: 'Vault entry',
  calendar: 'Calendar event',
  goal: 'Goal',
  progress: 'Goal progress',
  review: 'Weekly review',
  person: 'Person',
  focus: 'Focus session',
  'learning-card': 'Learning card',
  'learning-session': 'Learning session',
  settings: 'Settings',
  asset: 'Image',
};

/** What the record is, in the words the rest of the interface uses. */
export function kindLabel(kind: Kind, value: EntityValue) {
  if (kind === 'calendar') {
    return calendarTypes[text((value as Value).type)] ?? kindLabels.calendar;
  }

  return kindLabels[kind];
}

/**
 * The record's name: what a list or a heading calls it. A focus session is
 * named after its task when `names` knows it.
 */
export function recordTitle(kind: Kind, value: EntityValue, names: Record<string, string> = {}) {
  const v = value as Value;

  switch (kind) {
    case 'note':
      return text(v.title).trim() || 'Untitled';
    case 'person':
      return text(v.name);
    case 'calendar':
      return v.type === 'availability' ? `Availability for ${text(v.day)}` : text(v.title);
    case 'progress':
      return `Progress on ${text(v.date)}`;
    case 'review':
      return `Week of ${text(v.week)}`;
    case 'learning-card':
      return text(v.question);
    case 'learning-session':
      return `Review session on ${text(v.day)}`;
    case 'focus':
      return names[text(v.taskId)] ? `Session on “${names[text(v.taskId)]}”` : 'Focus session';
    case 'settings':
      return 'Workspace settings';
    case 'asset':
      return 'Image';
    default:
      return text(v.title);
  }
}

/** Names for relationship fields, keyed by record id. */
export function recordNames(records: { id: string; kind: Kind; value: EntityValue }[]) {
  const names: Record<string, string> = {};

  for (const record of records) {
    if (['note', 'task', 'goal', 'person'].includes(record.kind)) {
      names[record.id] = recordTitle(record.kind, record.value);
    }
  }

  return names;
}

/**
 * The properties that differ between this device's version and the server's,
 * in the order the record's own screen shows them. Properties are compared by
 * how they read, so a difference the owner could not see, such as the moment
 * each version was saved, produces no row.
 */
export function conflictFields(
  kind: Kind,
  local: EntityValue,
  remote: EntityValue,
  names: Record<string, string> = {},
  zone = calendarZone(),
): ConflictField[] {
  const readers = catalogue(kind, local as Value, names, zone);

  const known = new Set([
    ...readers.flatMap(reader => reader.covers ?? [reader.key.split('.')[0]]),
    ...ignored(kind),
  ]);

  const extra: Reader[] = [...new Set([...Object.keys(local), ...Object.keys(remote)])]
    .filter(key => !known.has(key))
    .sort()
    .map(key => ({ key, label: humanize(key), read: value => canonical(value[key]) }));

  // Two attachments can share a name and a size and still hold different
  // bytes. Say so rather than dropping the row as unchanged.
  const localFile = (local as Value).file as Value | undefined,
    remoteFile = (remote as Value).file as Value | undefined;

  const bytesDiffer =
    kind === 'vault' && Boolean(localFile && remoteFile) && localFile?.data !== remoteFile?.data;

  return [...readers, ...extra].flatMap(reader => {
    const field: ConflictField = {
      key: reader.key,
      label: reader.label,
      format: reader.format ?? 'text',
      local: reader.read(local as Value),
      remote: reader.read(remote as Value),
    };

    if (field.local !== field.remote) {
      return [field];
    }

    return field.key === 'file' && bytesDiffer
      ? [
          {
            ...field,
            local: `${field.local} · contents differ`,
            remote: `${field.remote} · contents differ`,
          },
        ]
      : [];
  });
}

/**
 * A line comparison of two long values: lines both versions share, then within
 * each changed stretch this device's lines before the other device's.
 *
 * It is a longest-common-subsequence walk over the lines left after trimming
 * the shared start and end, so the stretches the owner reads are as short as
 * they can be. A middle too large to compare line by line (more than four
 * million pairs) is shown as one changed stretch.
 */
export function conflictLines(local: string, remote: string): ConflictLine[] {
  // Empty text has no lines, not one empty line.
  const a = local ? local.split('\n') : [],
    b = remote ? remote.split('\n') : [];

  let start = 0;

  while (start < a.length && start < b.length && a[start] === b[start]) {
    start++;
  }

  let endA = a.length,
    endB = b.length;

  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }

  const lines: ConflictLine[] = a.slice(0, start).map(line => ({ side: 'both', text: line }));

  const middleA = a.slice(start, endA),
    middleB = b.slice(start, endB);

  const n = middleA.length,
    m = middleB.length;

  if (n * m > 4_000_000) {
    lines.push(...middleA.map(line => ({ side: 'local' as const, text: line })));
    lines.push(...middleB.map(line => ({ side: 'remote' as const, text: line })));
  } else {
    // lengths[i * (m + 1) + j] is the longest common run of middleA[i..] and middleB[j..].
    const lengths = new Int32Array((n + 1) * (m + 1));

    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        lengths[i * (m + 1) + j] =
          middleA[i] === middleB[j]
            ? lengths[(i + 1) * (m + 1) + j + 1] + 1
            : Math.max(lengths[(i + 1) * (m + 1) + j], lengths[i * (m + 1) + j + 1]);
      }
    }

    let i = 0,
      j = 0;

    while (i < n || j < m) {
      if (i < n && j < m && middleA[i] === middleB[j]) {
        lines.push({ side: 'both', text: middleA[i] });
        i++;
        j++;
      } else if (
        j >= m ||
        (i < n && lengths[(i + 1) * (m + 1) + j] >= lengths[i * (m + 1) + j + 1])
      ) {
        lines.push({ side: 'local', text: middleA[i] });
        i++;
      } else {
        lines.push({ side: 'remote', text: middleB[j] });
        j++;
      }
    }
  }

  lines.push(...a.slice(endA).map(line => ({ side: 'both' as const, text: line })));

  return lines;
}

function catalogue(kind: Kind, local: Value, names: Record<string, string>, zone: string) {
  const field = (
    key: string,
    label: string,
    read: (value: unknown) => string,
    format: ConflictField['format'] = 'text',
  ): Reader => ({ key, label, format, read: value => read(value[key]) });

  const nested = (
    parent: string,
    key: string,
    label: string,
    format: ConflictField['format'] = 'text',
  ): Reader => ({
    key: `${parent}.${key}`,
    label,
    format,
    read: value => text((value[parent] as Value | undefined)?.[key]),
  });

  const name = (id: unknown) => (typeof id === 'string' && id ? (names[id] ?? 'Unavailable') : '');
  const nameList = (ids: unknown) => list(ids).map(name).join('\n');

  const instant = (value: unknown) =>
    typeof value === 'string' && value ? localInput(value, zone).replace('T', ' ') : '';

  // Focus sessions keep their times as epoch milliseconds.
  const moment = (value: unknown) =>
    typeof value === 'number' && Number.isFinite(value)
      ? instant(new Date(value).toISOString())
      : '';

  switch (kind) {
    case 'note':
      return [
        field('title', 'Title', text),
        field('content', 'Content', value => blockText(value as JSONContent, true), 'long'),
        field('area', 'Area', text),
        field('parentId', 'Parent page', name),
        field('icon', 'Icon', text),
        field('iconTone', 'Icon background', text),
        field('locked', 'Locked', yesNo),
        field('trashed', 'In trash', yesNo),
      ];
    case 'task':
      return [
        field('title', 'Title', text),
        field('done', 'Completed', yesNo),
        field('completedOn', 'Completed on', text),
        field('dropped', 'Dropped', yesNo),
        field('today', 'On Today', yesNo),
        field('dueDate', 'Deadline', text),
        field('estimateMinutes', 'Estimate', value => withUnit(value, 'min')),
        field('personId', 'Waiting for', name),
        field('projectId', 'Project', name),
        field('areaId', 'Area', name),
        field('noteId', 'Working note', name),
        field(
          'steps',
          'Checklist',
          value =>
            list(value)
              .map(step => `${(step as Value).done ? '☑' : '☐'} ${text((step as Value).text)}`)
              .join('\n'),
          'long',
        ),
        field('stoppingPoint', 'Stopping point', text, 'long'),
        field('stoppingDraft', 'Unsaved stopping point', text, 'long'),
        field('waitingOn', 'Waiting on (earlier text)', text),
        field('focus', 'Focus timer', value => {
          const focus = value as Value | undefined;

          return focus
            ? `${focus.sessionOpen ? 'Session open' : 'No open session'}, ${Math.floor(Number(focus.elapsedMs) / 60000)} min recorded`
            : '';
        }),
        field('trashed', 'In trash', yesNo),
      ];
    case 'vault':
      return [
        field('title', 'Title', text),
        field('type', 'Entry type', value => vaultTypes[text(value) || 'login'] ?? text(value)),
        field('username', 'Username', text),
        field('password', 'Password', text, 'secret'),
        field('url', 'Website', text),
        nested('card', 'holder', 'Cardholder'),
        nested('card', 'number', 'Card number', 'secret'),
        nested('card', 'expiry', 'Expiry'),
        nested('card', 'cvv', 'Security code', 'secret'),
        nested('ssh', 'host', 'Host'),
        nested('ssh', 'publicKey', 'Public key', 'long'),
        nested('ssh', 'privateKey', 'Private key', 'secret'),
        nested('ssh', 'passphrase', 'Passphrase', 'secret'),
        field('file', 'Attachment', value => (value ? fileLabel(value as Value) : '')),
        field('projectId', 'Project', name),
        field('notes', 'Notes', text, 'long'),
        field('pinned', 'Pinned', yesNo),
        field('trashed', 'In trash', yesNo),
      ];
    case 'calendar': {
      const type = field('type', 'Event type', value => calendarTypes[text(value)] ?? text(value));

      if (local.type === 'availability') {
        return [
          type,
          field('day', 'Day', text),
          field(
            'windows',
            'Available times',
            value =>
              list(value)
                .map(w => `${instant((w as Value).startsAt)} – ${instant((w as Value).endsAt)}`)
                .join('\n'),
            'long',
          ),
          field('zone', 'Time zone', text),
        ];
      }

      if (local.type === 'birthday') {
        return [
          type,
          field('title', 'Title', text),
          {
            key: 'date',
            label: 'Date',
            covers: ['month', 'day'],
            read: (value: Value) =>
              typeof value.month === 'number'
                ? `${months[value.month - 1] ?? number(value.month)} ${number(value.day)}`
                : '',
          },
          field('reminderDays', 'Reminder', value =>
            typeof value !== 'number'
              ? ''
              : value === 0
                ? 'On the day'
                : `${number(value)} ${value === 1 ? 'day' : 'days'} before`,
          ),
          field('personId', 'Person', name),
          field('cancelled', 'Cancelled', yesNo),
          field('notes', 'Notes', text, 'long'),
        ];
      }

      return [
        type,
        field('title', 'Title', text),
        field('startsAt', 'Starts', instant),
        field('endsAt', 'Ends', instant),
        field('cancelled', 'Cancelled', yesNo),
        field('reminderMinutes', 'Reminder', value =>
          typeof value !== 'number'
            ? ''
            : value === 0
              ? 'At the start'
              : `${number(value)} min before`,
        ),
        field('recurrence', 'Repeats', value => {
          const rule = value as Value | undefined;

          if (!rule) {
            return '';
          }

          const unit =
            { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' }[
              text(rule.frequency)
            ] ?? text(rule.frequency);

          const every = Number(rule.interval) > 1 ? `${number(rule.interval)} ${unit}s` : unit;

          return `Every ${every}, ${number(Number(rule.index) + 1)} of ${number(rule.count)}`;
        }),
        field('personId', 'Person', name),
        field('taskId', 'Reserved for task', name),
        field('noteId', 'Linked note', name),
        field('followUpTaskIds', 'Follow-up tasks', nameList, 'long'),
        field('zone', 'Time zone', text),
        field('notes', 'Notes', text, 'long'),
      ];
    }
    case 'goal':
      return [
        field('title', 'Title', text),
        {
          key: 'target',
          label: 'Target',
          covers: ['target', 'unit'],
          read: (value: Value) => `${number(value.target)} ${text(value.unit)}`.trim(),
        },
        field('weeklyTarget', 'Weekly target', number),
        field('status', 'Status', capitalized),
        field('completedAt', 'Completed', instant),
        field('noteId', 'Linked note', name),
        field('taskIds', 'Linked tasks', nameList, 'long'),
      ];
    case 'focus':
      return [
        field('taskId', 'Task', name),
        {
          key: 'state',
          label: 'Session',
          covers: ['endedAt'],
          read: (value: Value) =>
            value.endedAt !== null && value.endedAt !== undefined
              ? 'Ended'
              : list(value.segments).some(segment => (segment as Value).endedAt === null)
                ? 'Running'
                : 'On a break',
        },
        field('startedAt', 'Started', moment),
        { key: 'ended', label: 'Ended', read: (value: Value) => moment(value.endedAt) },
        field(
          'segments',
          'Work intervals',
          value =>
            list(value)
              .map(item => {
                const segment = item as Value;
                const end = segment.endedAt === null ? 'running' : moment(segment.endedAt);

                const state =
                  segment.endedAt === null
                    ? ''
                    : segment.confirmed
                      ? ' · confirmed'
                      : ' · proposed';

                const corrected =
                  typeof segment.correctedMinutes === 'number'
                    ? ` · corrected to ${number(segment.correctedMinutes)} min`
                    : '';

                const note = text(segment.correctionNote);

                return `${moment(segment.startedAt)} – ${end}${state}${corrected}${segment.interrupted ? ' · interrupted' : ''}${note ? ` — ${note}` : ''}`;
              })
              .join('\n'),
          'long',
        ),
        field('offlineStart', 'Started without a connection', yesNo),
      ];
    case 'progress':
      return [
        field('goalId', 'Goal', name),
        field('date', 'Date', text),
        field('amount', 'Amount', number),
        field('taskId', 'Task', name),
        field('reversed', 'Reversed', yesNo),
        field('note', 'Note', text, 'long'),
      ];
    case 'review':
      return [
        field('week', 'Week of', text),
        field('completedAt', 'Completed', instant),
        field('reflection', 'Reflection', text, 'long'),
        field('decisions', 'Decisions', text, 'long'),
      ];
    case 'person':
      return [
        field('name', 'Name', text),
        field('contact', 'Contact', text),
        field('archived', 'Archived', yesNo),
        field('notes', 'Notes', text, 'long'),
        field(
          'gifts',
          'Gifts',
          value =>
            list(value)
              .map(item => {
                const gift = item as Value;

                const date = text(gift.date),
                  notes = text(gift.notes);

                return `${gift.status === 'given' ? 'Given' : 'Idea'}: ${text(gift.title)}${date ? ` (${date})` : ''}${notes ? ` — ${notes}` : ''}`;
              })
              .join('\n'),
          'long',
        ),
      ];
    case 'learning-card':
      return [
        field('question', 'Question', text, 'long'),
        field('answer', 'Answer', text, 'long'),
        field('additions', 'Additions', text, 'long'),
        field('source', 'Source', text, 'long'),
        field('status', 'Status', capitalized),
        field('noteId', 'Linked note', name),
        field('dueAt', 'Next review', instant),
        field('intervalDays', 'Interval', value => withUnit(value, value === 1 ? 'day' : 'days')),
        field('ease', 'Ease', number),
        field('reviews', 'Reviews', number),
        field('lapses', 'Lapses', number),
        field('lastReviewedAt', 'Last reviewed', instant),
        field('introducedAt', 'Introduced', instant),
        field('approvedAt', 'Approved', instant),
      ];
    case 'learning-session':
      return [
        field('day', 'Day', text),
        field('startedAt', 'Started', instant),
        field('deadline', 'Ends by', instant),
        field('endedAt', 'Ended', instant),
        field('reviewed', 'Cards reviewed', number),
      ];
    case 'settings':
      return [
        field('timezone', 'Time zone', text),
        field(
          'dailyChecks',
          'Daily load checks',
          value => {
            const checks = (value ?? {}) as Record<string, unknown>;

            return Object.keys(checks)
              .sort()
              .map(day => `${day}: ${capitalized(checks[day])}`)
              .join('\n');
          },
          'long',
        ),
      ];
    default:
      return [];
  }
}

/** Properties that change with every save and would only add noise. */
function ignored(kind: Kind) {
  return kind === 'note'
    ? ['updatedAt']
    : kind === 'calendar'
      ? ['history']
      : kind === 'focus'
        ? ['deviceId']
        : [];
}

function text(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function yesNo(value: unknown) {
  return value ? 'Yes' : 'No';
}

function capitalized(value: unknown) {
  const s = text(value);

  return s ? s[0].toUpperCase() + s.slice(1) : '';
}

function list(value: unknown) {
  return Array.isArray(value) ? value : [];
}

/**
 * Numbers as JavaScript prints them. The Swift port prints whole numbers
 * without a decimal point for the same reason: a stored 15 must read as 15.
 */
function number(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
}

function withUnit(value: unknown, unit: string) {
  const n = number(value);

  return n ? `${n} ${unit}` : '';
}

function fileLabel(file: Value) {
  const data = text(file.data),
    payload = data.slice(data.indexOf(',') + 1),
    padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0,
    bytes = Math.floor((payload.length * 3) / 4) - padding;

  return `${text(file.name) || 'Unnamed file'} · ${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function humanize(key: string) {
  const words = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();

  return words ? words[0].toUpperCase() + words.slice(1) : key;
}

/** A compact reading of any value, with object keys sorted so both platforms agree. */
function canonical(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'boolean') {
    return yesNo(value);
  }

  if (typeof value === 'number') {
    return number(value);
  }

  const nested = (item: unknown): string =>
    item === null || item === undefined
      ? 'null'
      : typeof item === 'string'
        ? `"${item.replaceAll('\\', '\\\\').replaceAll('"', '\\"').replaceAll('\n', '\\n')}"`
        : typeof item === 'object'
          ? canonical(item)
          : typeof item === 'boolean'
            ? String(item)
            : number(item);

  if (Array.isArray(value)) {
    return `[${value.map(nested).join(',')}]`;
  }

  const object = value as Value;

  return `{${Object.keys(object)
    .sort()
    .map(key => `"${key}":${nested(object[key])}`)
    .join(',')}}`;
}
